import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Repeat, Droplet, GitBranch, Send, TrendingUp, Star, UserCog, Sparkles, ArrowRight,
} from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/constants";

interface MovementData {
  disciples_in_dbs: number;
  multipliers: number;
  baptisms: number;
  believers: number;
  total_people: number;
  connected_people: number;
  generations: number;
  leaders: { disciple_makers: number; cdms: number; catalysts: number; campaign_managers: number };
  leads: { total: number; converted: number; conversion_rate: number };
  recent_baptisms: { id: string; name: string; baptised_at: number }[];
}

export function MovementMetrics() {
  const [data, setData] = useState<MovementData | null>(null);
  useEffect(() => {
    api.get<MovementData>("/dashboard/movement").then(setData).catch(() => {});
  }, []);
  if (!data) return null;

  return (
    <section className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-semibold flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-brand" /> Movement metrics
        </h2>
        <span className="text-xs text-ink-500">Tracking gospel multiplication, not just contacts.</span>
      </div>
      <p className="text-xs text-ink-500 mb-4">From Oikos relationships to Discovery Bible Studies, baptism, and multiplying disciples.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric
          icon={<BookOpen className="h-5 w-5" />}
          tint="text-blue-700 bg-blue-100"
          value={data.disciples_in_dbs}
          label="In Discovery Bible Study"
          sub={`of ${data.total_people} total`}
        />
        <Metric
          icon={<Droplet className="h-5 w-5" />}
          tint="text-sky-700 bg-sky-100"
          value={data.baptisms}
          label="Baptisms recorded"
          sub={data.believers > 0 ? `${data.believers} believers tracked` : undefined}
        />
        <Metric
          icon={<Repeat className="h-5 w-5" />}
          tint="text-brand-700 bg-brand-50"
          value={data.multipliers}
          label="Multiplying disciples"
          sub="Reproducing into others"
        />
        <Metric
          icon={<GitBranch className="h-5 w-5" />}
          tint="text-purple-700 bg-purple-100"
          value={data.generations}
          label="Generations deep"
          sub={`${data.connected_people} in a relational chain`}
        />
      </div>

      {/* Leader-kind breakdown */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <RoleStat icon={<UserCog className="h-4 w-4" />} value={data.leaders.disciple_makers} label="Disciple Makers" />
        <RoleStat icon={<Star className="h-4 w-4 text-brand" />} value={data.leaders.cdms} label="Campaign DMs" tint="text-brand" />
        <RoleStat icon={<Sparkles className="h-4 w-4 text-purple-600" />} value={data.leaders.catalysts} label="Catalysts" />
        <RoleStat icon={<Send className="h-4 w-4 text-blue-600" />} value={data.leaders.campaign_managers} label="Campaign Managers" />
      </div>

      {/* Lead pipeline summary */}
      {data.leads.total > 0 && (
        <div className="mt-5 rounded-lg bg-ink-50 p-4 text-sm flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-ink-500 uppercase tracking-wide">Lead → Person conversion</div>
            <div className="font-medium text-ink-900 mt-0.5">
              {data.leads.converted} of {data.leads.total} campaign leads converted
              <span className="ml-2 badge bg-brand-50 text-brand-700">{data.leads.conversion_rate}%</span>
            </div>
          </div>
          <Link to="/leads" className="text-sm text-brand hover:underline inline-flex items-center gap-1">
            Open leads <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {/* Recent baptisms */}
      {data.recent_baptisms.length > 0 && (
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2 flex items-center gap-1.5">
            <Droplet className="h-3 w-3 text-sky-600" /> Recent baptisms
          </div>
          <ul className="divide-y divide-ink-100">
            {data.recent_baptisms.map((b) => (
              <li key={b.id} className="py-2 text-sm flex items-center justify-between">
                <Link to={`/people/${b.id}`} className="font-medium text-ink-900 hover:text-brand">{b.name}</Link>
                <span className="text-xs text-ink-500">{fmtDate(b.baptised_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Metric({
  icon, tint, value, label, sub,
}: { icon: React.ReactNode; tint: string; value: number; label: string; sub?: string }) {
  return (
    <div className="rounded-lg bg-white ring-1 ring-ink-200 p-4">
      <div className="flex items-center justify-between">
        <span className={`shrink-0 h-9 w-9 rounded-full grid place-items-center ${tint}`}>{icon}</span>
        <span className="text-3xl font-semibold">{value}</span>
      </div>
      <div className="text-sm font-medium text-ink-900 mt-2">{label}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RoleStat({
  icon, value, label, tint = "text-ink-900",
}: { icon: React.ReactNode; value: number; label: string; tint?: string }) {
  return (
    <div className="rounded-md bg-ink-50 p-3 flex items-center gap-3">
      <span className="text-ink-400">{icon}</span>
      <div>
        <div className={`text-xl font-semibold ${tint}`}>{value}</div>
        <div className="text-[11px] text-ink-500 uppercase tracking-wide">{label}</div>
      </div>
    </div>
  );
}
